/**
 * Token Gating Context Provider
 * Global state management for token-gated features with caching and request deduplication
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { getAxiosInstance } from 'services/api';
import { useAuth } from 'store/auth/hooks';
import { useWallets } from '@privy-io/react-auth';

const api = getAxiosInstance();

export interface TokenGatedFeature {
  feature: string;
  name: string;
  description: string;
  hasAccess: boolean;
  tokenBalance: number;
  tokenRequired: number;
  missingTokens: number;
}

export interface TokenGatingStatus {
  walletConnected: boolean;
  walletAddress?: string;
  tokenBalance?: number;
  unlockedFeatures?: string[];
  lockedFeatures?: string[];
  features?: TokenGatedFeature[];
}

export interface WordLimitStatus {
  wordLimit: number;
  hasExtendedAccess: boolean;
  tokenBalance: number;
  tokenRequired: number;
  message: string;
}

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

interface TokenGatingContextValue {
  status: TokenGatingStatus | null;
  wordLimitStatus: WordLimitStatus | null;
  loading: boolean;
  error: string | null;
  fetchTokenStatus: () => Promise<void>;
  fetchWordLimitStatus: () => Promise<void>;
  refreshBalance: () => Promise<void>;
  hasFeatureAccess: (featureName: string) => boolean;
  getFeatureDetails: (featureName: string) => TokenGatedFeature | null;
  invalidateCache: () => void;
}

const TokenGatingContext = createContext<TokenGatingContextValue | undefined>(undefined);

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export const TokenGatingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { wallets, ready } = useWallets();

  const [status, setStatus] = useState<TokenGatingStatus | null>(null);
  const [wordLimitStatus, setWordLimitStatus] = useState<WordLimitStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cache and request tracking
  const statusCache = useRef<CacheEntry<TokenGatingStatus> | null>(null);
  const wordLimitCache = useRef<CacheEntry<WordLimitStatus> | null>(null);
  const statusRequestInFlight = useRef<Promise<void> | null>(null);
  const wordLimitRequestInFlight = useRef<Promise<void> | null>(null);

  const primaryWallet = wallets.find(w => w.connectorType === 'embedded') || wallets[0];

  // Check if cache is valid
  const isCacheValid = useCallback(<T,>(cache: CacheEntry<T> | null): boolean => {
    if (!cache) return false;
    return Date.now() - cache.timestamp < CACHE_TTL;
  }, []);

  // Invalidate cache manually
  const invalidateCache = useCallback(() => {
    statusCache.current = null;
    wordLimitCache.current = null;
  }, []);

  // Fetch token gating status with caching and deduplication
  const fetchTokenStatus = useCallback(async () => {
    if (!user?._id) return;

    // Return cached data if valid
    if (isCacheValid(statusCache.current)) {
      return;
    }

    // If request is already in flight, wait for it
    if (statusRequestInFlight.current) {
      await statusRequestInFlight.current;
      return;
    }

    // Start new request
    setLoading(true);
    setError(null);

    const requestPromise = (async () => {
      try {
        const response = await api.get('/token-gating/status');
        const data = response.data;

        setStatus(data);
        statusCache.current = {
          data,
          timestamp: Date.now(),
        };
      } catch (err: any) {
        console.error('Error fetching token status:', err);
        setError(err.response?.data?.message || 'Failed to fetch token status');
      } finally {
        setLoading(false);
        statusRequestInFlight.current = null;
      }
    })();

    statusRequestInFlight.current = requestPromise;
    await requestPromise;
  }, [user, isCacheValid]);

  // Fetch word limit status with caching and deduplication
  const fetchWordLimitStatus = useCallback(async () => {
    if (!user?._id) return;

    // Return cached data if valid
    if (isCacheValid(wordLimitCache.current)) {
      return;
    }

    // If request is already in flight, wait for it
    if (wordLimitRequestInFlight.current) {
      await wordLimitRequestInFlight.current;
      return;
    }

    // Start new request
    const requestPromise = (async () => {
      try {
        const response = await api.get('/token-gating/word-limit');
        const data = response.data;

        setWordLimitStatus(data);
        wordLimitCache.current = {
          data,
          timestamp: Date.now(),
        };
      } catch (err: any) {
        console.error('Error fetching word limit:', err);
      } finally {
        wordLimitRequestInFlight.current = null;
      }
    })();

    wordLimitRequestInFlight.current = requestPromise;
    await requestPromise;
  }, [user, isCacheValid]);

  // Refresh token balance
  const refreshBalance = useCallback(async () => {
    if (!user?._id) return;

    setLoading(true);
    try {
      await api.post('/token-gating/refresh-balance');

      // Invalidate cache to force fresh fetch
      invalidateCache();

      // Fetch fresh data
      await Promise.all([
        fetchTokenStatus(),
        fetchWordLimitStatus(),
      ]);
    } catch (err: any) {
      console.error('Error refreshing balance:', err);
      setError(err.response?.data?.message || 'Failed to refresh balance');
    } finally {
      setLoading(false);
    }
  }, [user, fetchTokenStatus, fetchWordLimitStatus, invalidateCache]);

  // Check if user has access to a feature from cached status
  const hasFeatureAccess = useCallback((featureName: string): boolean => {
    if (!status) return false;
    return status.unlockedFeatures?.includes(featureName) || false;
  }, [status]);

  // Get feature details from cached status
  const getFeatureDetails = useCallback((featureName: string): TokenGatedFeature | null => {
    if (!status?.features) return null;
    return status.features.find(f => f.feature === featureName) || null;
  }, [status]);

  // Initial fetch on mount and when wallet changes
  useEffect(() => {
    if (user?._id && ready) {
      // Fetch if we don't have valid cached data
      if (!isCacheValid(statusCache.current)) {
        fetchTokenStatus();
      } else {
        // Use cached data
        setStatus(statusCache.current!.data);
      }

      if (!isCacheValid(wordLimitCache.current)) {
        fetchWordLimitStatus();
      } else {
        // Use cached data
        setWordLimitStatus(wordLimitCache.current!.data);
      }
    }
  }, [user?._id, ready, primaryWallet?.address]);

  const value: TokenGatingContextValue = {
    status,
    wordLimitStatus,
    loading,
    error,
    fetchTokenStatus,
    fetchWordLimitStatus,
    refreshBalance,
    hasFeatureAccess,
    getFeatureDetails,
    invalidateCache,
  };

  return (
    <TokenGatingContext.Provider value={value}>
      {children}
    </TokenGatingContext.Provider>
  );
};

export const useTokenGatingContext = () => {
  const context = useContext(TokenGatingContext);
  if (context === undefined) {
    throw new Error('useTokenGatingContext must be used within a TokenGatingProvider');
  }
  return context;
};
